import React from "react";
import PropTypes from "prop-types";
import { TitleBlock } from "./TitleBlock";
import { ProgressBar } from "./ProgressBar";
import { StepList } from "./StepList";
import { StatusBlock } from "./StatusBlock";
import {
  getDisplayedTask,
  getActiveTaskIndices,
  getAdjecentTaskIds,
} from "./ProgressTracker.utils";
import "./style.scss";

export function ProgressTracker(props) {
  const [state, setState] = React.useState({
    displayedTask: undefined,
    activeTask: 1,
    activeStep: 0,
    prevTaskId: undefined,
    nextTaskId: undefined,
    progressBars: [],
  });

  const totalTasks = props.taskList.length;

  React.useEffect(() => {
    if (totalTasks) {
      const displayedTask = getDisplayedTask(props.taskList);

      const [prevTaskId, nextTaskId] = getAdjecentTaskIds(
        displayedTask.id,
        props.taskList.length
      );

      const [activeTask, activeStep] = getActiveTaskIndices(props.taskList);

      const progressBars = props.taskList.map((task) => ({
        id: task.id,
        filled: task.id <= displayedTask.id,
      }));

      setState({
        displayedTask,
        prevTaskId,
        nextTaskId,
        progressBars,
        activeStep,
        activeTask,
      });
    }
  }, [props.taskList]);

  const handleClick = (taskId) => {
    if (taskId) return () => props.setTaskVisible(taskId);
    else return undefined;
  };

  return state.displayedTask ? (
    <div className="progressTracker">
      <TitleBlock
        className="progressTracker_headblock"
        current={state.displayedTask.id}
        title={state.displayedTask.label}
      >
        <ProgressBar
          current={state.displayedTask.id}
          progressBars={state.progressBars}
          setTaskVisible={props.setTaskVisible}
        />
      </TitleBlock>
      <StepList
        taskId={state.displayedTask.id}
        steps={state.displayedTask.steps}
        className="progressTracker_stepBlock"
        addNote={props.addNote}
      />
      <StatusBlock
        className="progressTracker_statusBlock"
        activeTask={state.activeTask}
        activeStep={state.activeStep}
        onClickNext={handleClick(state.nextTaskId)}
        onClickPrevious={handleClick(state.prevTaskId)}
      />
    </div>
  ) : null;
}

ProgressTracker.propTypes = {
  /**
   * List of tasks
   */
  taskList: PropTypes.arrayOf(
    PropTypes.shape({
      /**
       * Boolean to set if task complete
       */
      complete: PropTypes.bool,
      /**
       * Index value of task
       */
      id: PropTypes.number,
      /**
       * label of list
       */
      label: PropTypes.string,
      /**
       * boolean to set if task is visible
       */
      visible: PropTypes.bool,
      /**
       * List of steps associalted with the task
       */
      steps: PropTypes.arrayOf(
        PropTypes.shape({
          /**
           * UniqueId for the step
           */
          stepId: PropTypes.number,
          /**
           * ariaLabel for the task
           */
          ariaLabel: PropTypes.string,
          /**
           * boolean to set if step complete
           */
          complete: PropTypes.bool,
          /**
           * boolean to set if the step is the active one
           */
          current: PropTypes.bool,
          /**
           * List of notes associated with step
           */
          notes: PropTypes.arrayOf(PropTypes.string),
          /**
           * description of task
           */
          task: PropTypes.string,
        })
      ),
    })
  ),
  /**
   * Function to toggle visible task
   */
  setTaskVisible: PropTypes.func,
  /**
   * Function to add new note to step
   */
  addNote: PropTypes.func,
};

ProgressTracker.defaultProps = {};
